import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Seeding one React Query entry from ANOTHER request's in-flight response — so a
 * consumer that would otherwise fire its own request joins that one instead.
 *
 * The existing way to reuse a response for a second key is `setQueryData` once the
 * response has ARRIVED. That is too late when both reads start together: the second key's
 * observer has already fetched in parallel. `seedQueryFromPending` puts a fetch on the
 * target key in the SAME tick the source request is sent, whose query function awaits the
 * source's promise. The target's observer then dedupes against it (React Query returns the
 * in-flight promise), so no second request goes out.
 *
 * ONE-SHOT, and that part is load-bearing. `query.fetch(options)` stores the options it was
 * given, this query function included, and a later `invalidateQueries` / `refetchQueries` —
 * which call `query.fetch(undefined)` — runs whatever function the entry stores. React Query 5
 * copies an observer's options onto the entry whenever the observer sets them (on creation and
 * on every render of a mounted `useQuery`), so a key whose observer joined the seed usually
 * refetches through its own fetcher. Until that happens, or when nothing observes the key, the
 * stored function is still THIS one. If it replayed the source promise it would hand back the
 * old, already-resolved response and send nothing. Only the first run reads the source; every
 * run after it calls `fallback`.
 *
 * `fallback` also covers the source failing, or answering without the part this key wants
 * (`pending` resolves `undefined`): the target then fetches on its own, as if never seeded.
 * It should be the same fetcher the target's own `useQuery` uses, for the same inputs the key
 * encodes. It is NOT called for a seed cancelled while it waited (its reader moved to another
 * key): nothing reads that entry any more.
 *
 * Consumers enabled in the same effect pass as the source's fetch may race it; only seed a
 * key `canSeedQuery` says is unclaimed, and let such a consumer hold its fetch one commit.
 */
export interface SeedQueryFromPendingOptions<TData> {
  queryKey: QueryKey;
  /** The source's in-flight response, narrowed to this key's data; `undefined` ⇒ not there. */
  pending: Promise<TData | undefined>;
  /**
   * The target's own fetcher — used when `pending` gives nothing, and for a later refetch
   * while the entry still stores the seed's query function.
   */
  fallback: (context: { signal?: AbortSignal }) => Promise<TData>;
  staleTime?: number;
}

/**
 * Seeds `queryKey` from the in-flight `pending` promise, once (see the module note above).
 *
 * @param {QueryClient} queryClient - The client holding the target entry.
 * @param {SeedQueryFromPendingOptions} options - The key, the source promise and the fallback fetcher.
 * @return {Promise<void>} The prefetch; never rejects (React Query's `prefetchQuery` swallows errors).
 */
export function seedQueryFromPending<TData>(
  queryClient: QueryClient,
  { queryKey, pending, fallback, staleTime }: SeedQueryFromPendingOptions<TData>
): Promise<void> {
  // A source failure is "no seed", handled here and now so it is never an unhandled rejection.
  let seed: Promise<TData | undefined> | null = pending.then(
    (data) => data,
    () => undefined
  );
  return queryClient.prefetchQuery<TData, Error>({
    queryKey,
    queryFn: async ({ signal }) => {
      const once = seed;
      seed = null;
      const seeded = once ? await once : undefined;
      if (seeded !== undefined) {
        return seeded;
      }
      // Cancelled while waiting (its reader moved to another key, which usually aborts the
      // source too): nobody wants this data, so start no fetch for it.
      if (signal?.aborted) {
        throw signal.reason;
      }
      return fallback({ signal });
    },
    staleTime,
  });
}

/**
 * Whether `queryKey` is waiting for data nobody is fetching yet — the only state it is safe
 * to seed in: an observer has created the entry (so something will read it), it has no data,
 * no fetch in flight, and no error (a failed entry is left to its own Retry).
 *
 * @param {QueryClient} queryClient - The client holding the target entry.
 * @param {QueryKey} queryKey - The target key.
 * @return {boolean} True when a seed would be read and would not race another fetch.
 */
export function canSeedQuery(
  queryClient: QueryClient,
  queryKey: QueryKey
): boolean {
  const state = queryClient.getQueryState(queryKey);
  return (
    state !== undefined &&
    state.data === undefined &&
    state.fetchStatus === 'idle' &&
    state.status !== 'error'
  );
}
